import { ScaffoldDropZone } from "../../../scaffold";
import { getFromDataPath, reduceDataPath } from "../../../utility";
import { isGroupingFactor } from "../../typeGuards";
import {
  ARSTActionDropAction,
  ArsTableCellData,
  ArsTableGroup,
  ArsTableSidebarItemKind,
  ArsTableState,
  BOTTOM_EDGE_ZONES,
} from "../interface";
import { isArsTableSubGroup, isArsTableSubGroupGroup } from "../typeguards/isArsTableSubGroup";
import { arst_groupingFactorToTableGroup, arst_tableGroupDepth } from "./arst_arsToTableGroup";
import { arst_insertTableGroups } from "./arst_insertTableGroups";
import { blankRowHeaderCell } from "./blankRowHeaderCell";

/**
 * Resolves the relative insertion path for row group insertion.
 *
 * @param targetCell - Scaffold cell that received the drop.
 * @param rows - Current root row groups.
 * @param isAfterZone - Whether the drop zone indicates insertion after the target.
 * @param isT - Label type guard.
 * @returns Relative insertion path for `arst_insertTableGroups`.
 */
export const arst_resolveRowInsertDataPath = <T>(
  targetCell: ArsTableCellData<T> | undefined,
  rows: ArsTableGroup<T>[],
  isAfterZone: boolean,
  isT: (v: unknown) => v is T,
): string => {
  if (!targetCell) return "/0";

  const pathParts = targetCell.dataPath.split("/").filter(Boolean);
  if (pathParts[0] !== "rows") return "/0";
  const relativePath = `/${pathParts.slice(1).join("/")}`;
  if (pathParts.length === 2) {
    const rootIndex = parseInt(pathParts[1], 10);
    return Number.isNaN(rootIndex) ? "/0" : `/${isAfterZone ? rootIndex + 1 : rootIndex}`;
  }

  const parentPath = reduceDataPath(relativePath, 2);
  const parentGroup = getFromDataPath(parentPath, rows, (obj) => isArsTableSubGroup(obj, isT));
  if (!parentGroup) return "/0";

  const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
  let isDataDriven = false;
  if (isArsTableSubGroupGroup(parentGroup, isT)) {
    isDataDriven = parentGroup.isDataDriven ?? false;
  } else if ("groupingFactor" in parentGroup) {
    isDataDriven = parentGroup.groupingFactor.dataDriven;
  }
  const currentSubGroups = parentGroup.subGroups ?? [];
  const insertAt = isDataDriven
    ? isAfterZone
      ? currentSubGroups.length
      : 0
    : isAfterZone
      ? childIndex + 1
      : childIndex;
  return Number.isNaN(insertAt) ? "/0" : `${parentPath}/subGroups/${insertAt}`;
};

/**
 * Inserts dropped grouping factors into the row hierarchy.
 *
 * @param state - Current ARS table state
 * @param action - Drop action describing the target row position
 * @returns Updated ARS table state
 */
export const arst_insertRow = <T>(
  state: ArsTableState<T>,
  action: ARSTActionDropAction,
): ArsTableState<T> => {
  const rowAfterZones = new Set<ScaffoldDropZone>([
    ...BOTTOM_EDGE_ZONES,
    ScaffoldDropZone.BottomLeft,
    ScaffoldDropZone.BottomRight,
  ]);

  const newTableGroups = action.payload
    .map((item) => item.dropItem)
    .filter((item) => isGroupingFactor(item))
    .map((groupingFactor) =>
      arst_groupingFactorToTableGroup(groupingFactor, state.settings.editorProps),
    );
  const targetCell = state.scaffoldModel?.cellData?.[action.rowId]?.[action.columnId];
  const insertDataPath = arst_resolveRowInsertDataPath(
    targetCell,
    state.bodyData.rows,
    rowAfterZones.has(action.zone),
    state.settings.editorProps.isT,
  );
  const insertedRows = arst_insertTableGroups(
    state.bodyData.rows,
    newTableGroups,
    insertDataPath,
    state.settings.editorProps.isT,
  );
  const newBodyData = { ...state.bodyData, rows: insertedRows };

  const newRowDepth = Math.max(
    ...newBodyData.rows.map((group) => arst_tableGroupDepth(group, state.settings.editorProps)),
    1,
  );
  const rowHeaderTemplate = blankRowHeaderCell(
    state.settings.editorProps.blankT,
    newBodyData.rowHeaders.length > 0
      ? newBodyData.rowHeaders[0].heights
      : (state.scaffoldModel?.rows.filter((row) => row.type === "header").length ?? 1),
  );
  const newRowHeaders = [
    ...newBodyData.rowHeaders,
    ...Array.from({ length: newRowDepth - newBodyData.rowHeaders.length }, () => ({
      ...rowHeaderTemplate,
    })),
  ];

  const newUsedGroupingFactorIds = new Set(state.usedGroupingFactorIds);
  action.payload.forEach((item) => {
    if (item.kind === ArsTableSidebarItemKind.GroupingFactor) {
      newUsedGroupingFactorIds.add(item.dropItem.id);
    }
  });

  return {
    ...state,
    bodyData: { ...newBodyData, rowHeaders: newRowHeaders },
    usedGroupingFactorIds: newUsedGroupingFactorIds,
    changed: true,
  };
};
